import { useSetSearch } from '@luminovo/design-system';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod/v4';

import { OrderDetailsPage } from '@/areas/orders/Orders/pages/OrderDetailsPage';
import { OrderListPage } from '@/areas/orders/Orders/pages/OrderListPage';
import { canViewOrders } from '@/shared/permissions/pagePermissions';
import type { PageStaticData } from '@/shared/routing';
import { SidebarPageContent } from '@/shared/ui/PageLayoutCollapsibleSidebar';

// Not a copy of today's `/orders/{-$orderId}`, which draws the list beside the order: here the list and an order are
// two pages. Still one route with an optional param, since the reroute from today's route passes its params on as they
// are. Links to the list pass `orderId: undefined`, or the router keeps the id.
export const Route = createFileRoute('/_authenticated/next/orders/{-$orderId}')({
    staticData: { access: { permissions: canViewOrders }, chrome: 'none' } satisfies PageStaticData,
    // The table's search, sorting and filters are `_authenticated`'s params.
    validateSearch: z.object({ customerPartNumber: z.string().optional() }),
    component: RouteComponent,
});

function RouteComponent(): JSX.Element {
    const { orderId } = Route.useParams();
    const customerPartNumber = Route.useSearch({ select: (search) => search.customerPartNumber });
    const setSearch = useSetSearch();
    // An order draws its own breadcrumbs header, so it wraps itself in `SidebarPageContent`.
    return orderId === undefined ? (
        <SidebarPageContent layoutVariant="fullWidth">
            <OrderListPage
                customerPartNumber={customerPartNumber}
                onCustomerPartNumberChange={(value) => setSearch({ customerPartNumber: value })}
            />
        </SidebarPageContent>
    ) : (
        <OrderDetailsPage orderId={orderId} customerPartNumber={customerPartNumber} />
    );
}
